/**
 * Pure boot-gate decision for the install-landing vs. full-shell split (#1815 stage 3 of 3).
 *
 * Kept free of every browser global (`location`, `sessionStorage`, `matchMedia`, …) so it's
 * unit-testable without a DOM — `system/pwa/src/main.ts` is the only caller, and it owns
 * reading each of these inputs from the real browser environment (`installEnv.isStandalone()`,
 * `URLSearchParams(location.search)`, `sessionStorage`) before calling in here. Mirrors the
 * split `version-compatibility.ts` already uses in this same directory: browser I/O in the
 * composition root, decision logic here as a plain function of its inputs.
 */
export interface BootGateInputs {
  /** `env.isStandalone()` — the page is already running as an installed PWA. */
  readonly isStandalone: boolean;
  /** `?shell=1` OR `?onboarding=1` present in the URL — a dev/QA override that forces the
   * full shell to boot without going through the install landing. */
  readonly hasShellOverride: boolean;
  /** The user already clicked "Continue in browser" earlier this session (the escape-hatch
   * flag persisted to `sessionStorage` under `CONTINUE_IN_BROWSER_KEY`). */
  readonly continuedInBrowser: boolean;
}

/**
 * Boot the full shell directly (skip the install landing) when launched as an installed PWA,
 * when a dev override forces the shell, or when the user already chose "Continue in browser"
 * this session.
 */
export function shouldBootShell(inputs: BootGateInputs): boolean {
  return inputs.isStandalone || inputs.hasShellOverride || inputs.continuedInBrowser;
}

/** `sessionStorage` key for the "Continue in browser" escape-hatch flag. Session-scoped (not
 * `localStorage`) so a visitor who continues in browser today still sees the install pitch on
 * a fresh visit tomorrow — only THIS tab's session skips it going forward. */
export const CONTINUE_IN_BROWSER_KEY = "client-node.continue-in-browser";
